import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  NOTIFICATIONS_PERSONAL_KEY,
  NOTIFICATIONS_PREFERENCES_KEY,
  NOTIFICATIONS_UNREAD_KEY,
  type NotificationPreferencesResponse,
  type NotificationPreferencesUpdate,
  type PersonalNotification,
  type UnreadCount,
} from './types'

export function useUnreadNotificationCount(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_KEY,
    queryFn: async () => {
      const { data } = await api.get<UnreadCount>('/notifications/unread-count')
      return data
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function usePersonalNotifications(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_PERSONAL_KEY,
    queryFn: async () => {
      const { data } = await api.get<PersonalNotification[]>('/notifications/personal', {
        params: { limit: 40 },
      })
      return data
    },
    enabled,
    staleTime: 10_000,
    refetchInterval: enabled ? 30_000 : false,
    refetchOnWindowFocus: true,
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<PersonalNotification>(`/notifications/personal/${id}/read`)
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY })
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_PERSONAL_KEY })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ marked: number }>('/notifications/personal/read-all')
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY })
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_PERSONAL_KEY })
    },
  })
}

export function useNotificationPreferences(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_PREFERENCES_KEY,
    queryFn: async () => {
      const { data } = await api.get<NotificationPreferencesResponse>(
        '/notifications/preferences',
      )
      return data
    },
    enabled,
    staleTime: 30_000,
  })
}

export function useUpdateNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: NotificationPreferencesUpdate) => {
      const { data } = await api.put<NotificationPreferencesResponse>(
        '/notifications/preferences',
        body,
      )
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData(NOTIFICATIONS_PREFERENCES_KEY, data)
    },
  })
}
